import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/clientApp';
import { useUser } from './useUser';
import {
  collection,
  orderBy,
  limit,
  query,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { functions } from '../../firebase/clientApp';
import { httpsCallable } from 'firebase/functions';

const NotificationsContext = React.createContext();

function NotificationsProvider({ children }) {
  const { user } = useUser();
  const [notifications, setNotifications] = useState([]);
  const [newNotifications, setNewNotifications] = useState(false);

  useEffect(() => {
    if (!user?.uid) return;

    const notificationsQuery = query(
      collection(db, `users/${user.uid}/notifications`),
      orderBy('createdAt', 'desc'),
      limit(30)
    );

    const subscribeToNotifications = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => {
          return { ...doc.data(), id: doc.id };
        });
        if (data.length) {
          const sortedData = data.sort((x, y) => {
            return x.read === y.read ? 0 : x.read ? 1 : -1;
          });
          setNotifications(sortedData);
          setNewNotifications(
            sortedData.filter((notification) => !notification.read).length
          );
        }
      },
      (error) => {
        console.error('Error loading notifications: ', error);
      }
    );

    return () => subscribeToNotifications();
  }, [user]);

  const markNotificationAsRead = async (notificationId, propertySharingUrl) => {
    if (!propertySharingUrl) {
      try {
        const docRef = doc(
          db,
          `users/${user.uid}/notifications`,
          notificationId
        );
        await updateDoc(docRef, { read: true });
      } catch (error) {
        console.error('Error marking notification as read: ', error);
      }
    } else {
      try {
        const parsed = new URL(propertySharingUrl);
        const propertyId = parsed.pathname.split('/')[3];
        const userId = parsed.pathname.split('/')[2];
        const onReadSharingNotification = httpsCallable(
          functions,
          'onReadSharingNotification'
        );

        await onReadSharingNotification({ propertyId, userId, notificationId });
      } catch (error) {
        console.error('Error marking notification as read: ', error);
      }
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const docRef = doc(db, `users/${user.uid}/notifications`, notificationId);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting notification: ', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const batch = writeBatch(db);
      notifications.forEach((notification) => {
        const docRef = doc(
          db,
          `users/${user.uid}/notifications`,
          notification.id
        );
        batch.update(docRef, { read: true });
      });
      await batch.commit();
    } catch (error) {
      console.error('Error marking all notifications as read: ', error);
    }
  };

  const deleteAllNotifications = async () => {
    try {
      const batch = writeBatch(db);
      notifications.forEach((notification) => {
        const docRef = doc(
          db,
          `users/${user.uid}/notifications`,
          notification.id
        );
        batch.delete(docRef);
      });
      await batch.commit();
      setNotifications([]);
    } catch (error) {
      console.error('Error deleting all notifications: ', error);
    }
  };

  const value = {
    notifications,
    newNotifications,
    markNotificationAsRead,
    deleteNotification,
    markAllAsRead,
    deleteAllNotifications,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

function useNotifications() {
  const context = React.useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationsProvider'
    );
  }
  return context;
}

export { NotificationsProvider, useNotifications };
